import React, { useState, useRef } from "react";
import { defineBlock, smartProps, ChildrenBlock, EditableImg, useEditor, useInspectorControl, useSmartProp } from "@smartfire/gutenberg-react";
import { InnerBlocks } from '@wordpress/block-editor';
import { PanelBody, PanelRow, ToggleControl } from '@wordpress/components';
import EditableVideo from '../shared/EditableVideo/index';
// Style imports
import style from "./block.module.scss";
import classnames from 'classnames';
import { ReactComponent as Play } from "../assets/icons/play.svg";
import { ReactComponent as ParabLeft } from './parabola-left.svg';
import { ReactComponent as ParabRight } from './parabola-right.svg';

import CloseIcon from '@material-ui/icons/Close';
import Modal from '@material-ui/core/Modal';
import Backdrop from '@material-ui/core/Backdrop';
import Fade from '@material-ui/core/Fade';

const grey = "#FAF8F8";

/**
 * The Inspector is used to set the position of the columns, and the type of media.
 */
const Inspector = () => {
    const [isReversed, setReverse] = useSmartProp("isReversed");
    const [image] = useSmartProp("image");
    const [addVideo, setAddVideo] = useSmartProp("addVideo");
    const [optionnalBackground, setOptionnalBackground] = useSmartProp("optionnalBackground");

    return (<>
        <PanelBody title="Inverser les colonnes" initialOpen={true}>
            <PanelRow>
                <ToggleControl
                    label={isReversed ? 'Image à droite' : 'Image à gauche'}
                    checked={isReversed}
                    onChange={setReverse}
                />
            </PanelRow>
        </PanelBody>

        <PanelBody title="Afficher le fond avec des paraboles" initialOpen={true}>
            <PanelRow>
                <ToggleControl
                    label={optionnalBackground ? "Avec" : "Sans"}
                    checked={optionnalBackground}
                    onChange={(val) => setOptionnalBackground(val)}
                />
            </PanelRow>
        </PanelBody>

        { image && <>
            <PanelBody title="Ajouter une vidéo" initialOpen={true}>
                <PanelRow>
                    <ToggleControl
                        label={addVideo ? "Oui" : "Non"}
                        checked={addVideo}
                        onChange={(val) => setAddVideo(val)}
                    />
                </PanelRow>
            </PanelBody>
        </>}
    </>);
}

/**
 * Two Columns Block
 * It contains a column media and a column with text content. The media can be
 * an image or a video.
 */
const Block = ({ isReversed, addVideo, optionnalBackground, image }) => {
    const { isEditing } = useEditor();
    useInspectorControl(Inspector);

    const videoRef = useRef(null);
    const [open, setOpen] = useState(false);

    // Classes of the main block
    const blockClasses = classnames({
        [style.block]: true,
        [style.reverse]: isReversed,
    });

    // Open the video popup
    const handleOpen = () => {
        setOpen(true);
    };

    // Close the video popup
    const handleClose = () => {
        setOpen(false);
        // Reset video
        if (videoRef && videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        }
    };

    return (
        <div className={blockClasses} style={{backgroundColor: optionnalBackground && grey}}>
            { optionnalBackground && <>
                <ParabLeft className={ style.picto1 }/>
                <ParabRight className={ style.picto2 }/>
            </>}

            <div className={style.container}>
                <div className={style.colMedia}>
                    <div className={style.imgContainer}>
                        { !addVideo ? (
                            <EditableImg smartProp="image" />
                        ) : ( isEditing ? (
                                <EditableVideo className={style.video} smartProp="video" poster={image} />
                            ) : (
                                <>
                                    <EditableImg smartProp="image" />
                                    <div className={style.videoBackground} />
                                </>
                            )
                        )}
                    </div>

                    { addVideo && !isEditing &&
                        <div className={style.playButton} onClick={!isEditing && handleOpen}>
                            <Play />
                        </div>
                    }
                </div>

                <div className={style.colText}>
                    { isEditing ? (
                            <InnerBlocks
                                allowedBlocks={['core/heading', 'core/paragraph', 'smartfire/button', 'smartfire/spacer']}
                                template={[
                                    ['core/heading', { level: 4, content: 'Subtitle' }],
                                    ['core/heading', { level: 3, content: 'Lorem Ipsum' }],
                                    ['core/paragraph', { placeholder: 'Enter text here...' }],
                                ]}
                            />
                    ) : (
                        <ChildrenBlock />
                    )}
                </div>
            </div>

            {!isEditing && addVideo && (
                <Modal
                    className={classnames(style.modal)}
                    open={open}
                    onClose={handleClose}
                    closeAfterTransition
                    BackdropComponent={Backdrop}
                    BackdropProps={{
                        timeout: 500,
                    }}
                >
                    <Fade in={open}>
                        <div className={classnames(style.videoContainer)}>
                            <CloseIcon className={style.modalClose} onClick={handleClose} />
                            <EditableVideo
                                className={style.video}
                                smartProp="video"
                                poster={image}
                                domRef={videoRef}
                                autoplay={true}
                                controls={true}
                            />
                        </div>
                    </Fade>
                </Modal>
            )}
        </div>
    );
}

export default defineBlock({
    Block,
    smartProps: {
        isReversed: smartProps.bool.withDefault(false),
        image: smartProps.image,
        video: smartProps.any,
        addVideo: smartProps.bool.withDefault(false),
        optionnalBackground: smartProps.bool.withDefault(true),
    },
    setup: {
        title: '2 Colonnes',
    }
});
